import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@apollo/client";
import {
  CreateFavoriteContentMutation,
  DeleteFavoriteContentMutation,
} from "arena/mutations";
import { FavoriteContentQuery } from "arena/queries";
import { useTranslation } from "react-i18next";
import SkuflixBookmarkIcon from "core/icons/skuflix/SkuflixBookmarkIcon";
import { useViewer } from "core/components/ViewerProvider";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export default function FavoriteButton({ contentId, compact = false }) {
  const { t } = useTranslation("arena");
  const viewer = useViewer();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const isCompact = compact && !sm;

  const [createFavoriteContent] = useMutation(CreateFavoriteContentMutation, {
    update(cache) {
      cache.modify({
        fields: {
          favoriteContent() {
            return true;
          },
        },
      });
    },
    variables: {
      contentId,
    },
  });

  const [deleteFavoriteContent] = useMutation(DeleteFavoriteContentMutation, {
    update(cache) {
      cache.modify({
        fields: {
          favoriteContent(favoriteContentRefs, { DELETE }) {
            return DELETE;
          },
        },
      });
    },
    variables: {
      contentId,
    },
  });

  const { data } = useQuery(FavoriteContentQuery, {
    variables: {
      contentId,
    },
  });

  const isFavorite = data?.favoriteContent;

  const onClick = () => {
    if (!viewer) return document.querySelector(".js-sign-in").click();
    if (isFavorite) deleteFavoriteContent();
    else createFavoriteContent();
  };

  const buttonSize = isCompact ? "small" : sm ? "large" : "medium";

  return (
    <Button
      variant="text"
      color="inherit"
      startIcon={
        <SkuflixBookmarkIcon
          color={isFavorite ? "primary" : "inherit"}
          sx={isCompact ? { fontSize: 18 } : undefined}
        />
      }
      size={buttonSize}
      onClick={onClick}
      sx={{
        whiteSpace: "nowrap",
        borderRadius: 0,
        ...(isCompact && {
          minWidth: 0,
          px: 1,
          fontSize: "0.8125rem",
          "& .MuiButton-startIcon": {
            marginRight: 0.5,
          },
        }),
      }}
    >
      {isFavorite ? t("favoriteButton.saved") : t("favoritButton.save")}
    </Button>
  );
}

FavoriteButton.propTypes = {
  contentId: PropTypes.string.isRequired,
  compact: PropTypes.bool,
};
